import axios from "axios";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const MODULO = "TarjetaUID";

const RegistrarTarjetaAprendiz = () => {
  const [documentoAprendiz, setDocumentoAprendiz] = useState("");
  const [nombreAprendiz, setNombreAprendiz] = useState("");
  const [tarjetaUID, setTarjetaUID] = useState("");
  const [aprendizId, setAprendizId] = useState("");
  const [verificando, setVerificando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const navigate = useNavigate();

  const normalizarTexto = (texto = "") =>
    texto
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "")
      .toLowerCase();

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const obtenerPermisosModulo = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      return (
        modulos.find((modulo) => {
          const nombreModulo =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombreModulo) === normalizarTexto(MODULO);
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const permisosModulo = obtenerPermisosModulo();

  const puedeCrear =
    permisosModulo?.puedeCrear === true || permisosModulo?.crear === true;

  const isVerified = useMemo(
    () => Boolean(aprendizId && nombreAprendiz),
    [aprendizId, nombreAprendiz]
  );

  const cleanData = useCallback(() => {
    setDocumentoAprendiz("");
    setNombreAprendiz("");
    setTarjetaUID("");
    setAprendizId("");
  }, []);

  const goBackToList = useCallback(() => {
    navigate("/tarjeta/listartarjetaaprendiz");
  }, [navigate]);

  const verificarDocumentoAprendiz = useCallback(async (documento) => {
    const doc = String(documento || "").trim();
    if (!doc) return;

    try {
      setVerificando(true);

      const response = await axios.get(
        `/api/aprendices/verificar/${doc}`,
        authConfig()
      );

      if (response.data?.exists) {
        setNombreAprendiz(response.data?.nombreCompleto || "");
        setAprendizId(response.data?.aprendizId || "");
      } else {
        setNombreAprendiz("");
        setAprendizId("");

        Swal.fire({
          icon: "error",
          title: "Aprendiz no encontrado",
          text: "El documento ingresado no corresponde a ningún aprendiz.",
        });
      }
    } catch (error) {
      console.error("Error al verificar el documento del aprendiz:", error);

      setNombreAprendiz("");
      setAprendizId("");

      Swal.fire({
        icon: "error",
        title: "Error del servidor",
        text: "No se pudo verificar el documento.",
      });
    } finally {
      setVerificando(false);
    }
  }, []);

  const saveTarjeta = useCallback(async () => {
    if (!puedeCrear) {
      return Swal.fire({
        icon: "warning",
        title: "Sin permisos",
        text: "No tiene permisos para asignar tarjetas.",
      });
    }

    if (!aprendizId || !tarjetaUID.trim()) {
      return Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Debes verificar el documento y escribir el UID de la tarjeta.",
      });
    }

    try {
      setGuardando(true);

      const nuevaAsignacion = {
        aprendiz: aprendizId,
        tarjetaUID: tarjetaUID.trim(),
      };

      await axios.post("/api/tarjetas/add", nuevaAsignacion, authConfig());

      Swal.fire({
        icon: "success",
        title: "¡Tarjeta asignada correctamente!",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      goBackToList();
    } catch (error) {
      console.error("Error al asignar la tarjeta:", error);

      Swal.fire({
        icon: "error",
        title: "Error al asignar la tarjeta",
        text: error.response?.data?.message || "Error desconocido",
      });
    } finally {
      setGuardando(false);
    }
  }, [aprendizId, tarjetaUID, cleanData, goBackToList, puedeCrear]);

  const actions = async (e) => {
    e.preventDefault();
    await saveTarjeta();
  };

  const handleDocumentoAprendizChange = (e) => {
    setDocumentoAprendiz(e.target.value);
    setNombreAprendiz("");
    setAprendizId("");
  };

  const handleDocumentoAprendizKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      verificarDocumentoAprendiz(documentoAprendiz);
    }
  };

  const handleDocumentoAprendizBlur = () => {
    if (documentoAprendiz) verificarDocumentoAprendiz(documentoAprendiz);
  };

  const disabledAll = verificando || guardando || !puedeCrear;

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">Asignar Tarjeta</h5>
                <small className="text-secondary">
                  Verifica el aprendiz por documento y asigna el UID de la
                  tarjeta RFID.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={goBackToList}
                  disabled={verificando || guardando}
                  title="Regresar"
                >
                   <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>

                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={cleanData}
                  disabled={disabledAll}
                  title="Limpiar"
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {!puedeCrear && (
              <div className="alert alert-warning mb-4">
                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                No tiene permisos para asignar tarjetas en este módulo.
              </div>
            )}

            {(verificando || guardando) && (
              <div className="text-center text-secondary py-2">
                <div className="spinner-border" role="status" />
                <div className="mt-2">
                  {verificando
                    ? "Verificando aprendiz…"
                    : "Asignando tarjeta…"}
                </div>
              </div>
            )}

            <form
              id="form-tarjeta"
              onSubmit={actions}
              className="needs-validation"
              noValidate
            >
              <div className="row g-3 align-items-end">
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">
                    Documento Aprendiz
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-regular fa-id-card"></i>
                    </span>

                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ej: 123456789"
                      value={documentoAprendiz}
                      onChange={handleDocumentoAprendizChange}
                      onBlur={handleDocumentoAprendizBlur}
                      onKeyDown={handleDocumentoAprendizKeyDown}
                      required
                      disabled={disabledAll}
                    />

                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        verificarDocumentoAprendiz(documentoAprendiz)
                      }
                      disabled={disabledAll || !documentoAprendiz.trim()}
                      title="Verificar"
                    >
                      <i
                        className={`fa-solid ${
                          verificando
                            ? "fa-spinner fa-spin"
                            : "fa-magnifying-glass"
                        } me-2`}
                      ></i>
                      Verificar
                    </button>
                  </div>

                  <div className="form-text">
                    Presiona <b>Enter</b> o usa <b>Verificar</b>.
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">
                    Nombre Aprendiz
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-regular fa-user"></i>
                    </span>

                    <input
                      type="text"
                      className="form-control"
                      placeholder="Se completa automáticamente"
                      value={nombreAprendiz}
                      readOnly
                      disabled
                    />

                    <span
                      className={`input-group-text ${
                        isVerified ? "text-success" : "text-secondary"
                      }`}
                    >
                      <i
                        className={`fa-solid ${
                          isVerified ? "fa-circle-check" : "fa-circle-info"
                        }`}
                      ></i>
                    </span>
                  </div>

                  <div className="form-text">
                    {isVerified
                      ? "Aprendiz verificado correctamente."
                      : "Primero verifica el documento."}
                  </div>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">
                    UID de la Tarjeta
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fa-solid fa-id-badge"></i>
                    </span>

                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ej: 04A1B2C3D4"
                      value={tarjetaUID}
                      onChange={(e) => setTarjetaUID(e.target.value)}
                      required
                      disabled={disabledAll || !isVerified}
                    />
                  </div>

                  <div className="form-text">
                    {isVerified
                      ? "Ingresa o escanea el UID."
                      : "Se habilita al verificar el aprendiz."}
                  </div>
                </div>

                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={goBackToList}
                      disabled={verificando || guardando}
                      title="Cancelar"
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      form="form-tarjeta"
                      className="btn btn-dark"
                      disabled={
                        disabledAll || !isVerified || !tarjetaUID.trim()
                      }
                      title={
                        puedeCrear
                          ? "Asignar"
                          : "Sin permiso para asignar tarjeta"
                      }
                    >
                      <i
                        className={`fa-solid ${
                          guardando ? "fa-spinner fa-spin" : "fa-link"
                        } me-2`}
                      ></i>
                      Asignar Tarjeta
                    </button>
                  </div>
                </div>
              </div>
            </form>

            <hr className="my-4" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarTarjetaAprendiz;